/**
 * Pure projection from the builder's working element shape (CanvasBuilderElement)
 * to the renderer's RenderableElement, used by the builder preview (spec 7.2
 * preview seam).
 *
 * This lives outside CanvasBuilder so the projection is testable in isolation.
 * The builder loader preserves each element's parent linkage and order; the
 * preview projection must carry those three fields (parentElementId,
 * parentPanelId, displayOrder) through, or the CanvasRenderer sees no parent on a
 * nested child and mounts it as a grid ROOT instead of inside its container
 * (audit finding 1: "builder preview drops nesting metadata").
 */
import type { RenderableElement } from "../components/canvasBuilder";
import type {
  CanvasElement,
  VisibilityRules,
  AutoFillConfig,
  ValidationRule,
} from "../types";

export interface CanvasBuilderElement extends CanvasElement {
  tempId: string;
  gridColumn: number;
  gridRow: number;
  columnSpan: number;
  rowSpan: number;
  // Nesting support
  parentElementId?: string;
  parentPanelId?: string;
  displayOrder?: number;
  // Enhanced properties
  visibilityRules?: VisibilityRules;
  autoFill?: AutoFillConfig;
  validationRules?: ValidationRule[];
  /** Replacement-owned fields that must survive a full document save. */
  name?: string;
  isVisible?: boolean;
  styleOverrides?: Record<string, unknown>;
  cssClasses?: string;
}

/**
 * Transform builder elements into RenderableElement form for preview mode.
 * Carries the nesting fields so nested children stay nested in the preview.
 */
export function projectRenderableElements(
  elements: CanvasBuilderElement[],
): RenderableElement[] {
  return elements.map((el) => ({
    id: el.tempId,
    type: el.type,
    variant: el.props.variant as string,
    props: el.props,
    gridColumn: el.gridColumn,
    gridRow: el.gridRow,
    columnSpan: el.columnSpan,
    rowSpan: el.rowSpan,
    data_binding: el.data_binding
      ? {
          data_source_id: el.data_binding.data_source_id,
          field_path: el.data_binding.field_path,
        }
      : undefined,
    // Nesting support. The loader preserves parent linkage and order on each
    // builder element; carry them through so the renderer mounts nested children
    // under their container instead of promoting them to grid roots.
    parentElementId: el.parentElementId,
    parentPanelId: el.parentPanelId,
    displayOrder: el.displayOrder,
    styleOverrides: el.styleOverrides,
    isVisible: el.isVisible,
    visibilityRules: el.visibilityRules,
  }));
}
